import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { stats, testimonials } from '@/mocks/homeContent';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim() || '';
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9rcg3fnahb7382ovkgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 禁漫天堂 | 品牌介绍·用户口碑·联系方式"
        description="禁漫天堂是国内领先的漫画阅读平台，汇聚海量正版授权漫画资源。了解我们的品牌故事、发展历程、用户口碑评价，以及联系方式和客服支持。"
        keywords="禁漫天堂关于我们,禁漫天堂品牌介绍,禁漫天堂联系方式,禁漫天堂用户评价"
        canonicalUrl="https://jmtt.org/about"
        schemaJsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 禁漫天堂',
            url: 'https://jmtt.org/about',
            description: '禁漫天堂品牌介绍、用户口碑及联系方式',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmtt.org/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://jmtt.org/about' },
            ],
          },
        ]}
      />

      <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

      <main className="w-full pb-16">
        {/* Brand Intro */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6 text-center">
              关于禁漫天堂
            </h1>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4">
                <p>
                  禁漫天堂成立于2020年，是国内领先的综合性漫画阅读平台。我们致力于为广大漫画爱好者打造一个安全、便捷、高品质的漫画阅读空间，让每一位用户都能在这里找到属于自己的阅读乐趣。
                </p>
                <p>
                  平台目前已收录超过<strong>50,000部</strong>正版授权漫画作品，涵盖热血少年、恋爱甜宠、奇幻冒险、动作格斗、搞笑日常、悬疑推理、科幻未来等全品类内容。无论是经典巨作还是最新连载，禁漫天堂都能满足你的阅读需求。
                </p>
                <p>
                  我们始终坚持<strong>「用户至上、内容为王」</strong>的核心理念。在内容方面，与国内外多家知名出版社和作者建立了深度合作关系，确保所有作品均为正版授权；在技术方面，采用先进的阅读引擎和CDN加速技术，保障流畅的阅读体验；在服务方面，提供7x24小时全天候客服支持。
                </p>
                <p>
                  截至2026年，禁漫天堂已累计服务<strong>超过1,200万</strong>注册用户，日均活跃用户超过300万。凭借优质的内容和服务，我们赢得了广大用户的信赖与好评。未来，禁漫天堂将继续深耕漫画领域，为更多漫画爱好者带来优质的阅读体验。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">
              平台数据
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="text-center bg-background-50 rounded-xl p-5">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</div>
                  <div className="text-sm text-foreground-500 mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-100 rounded-xl p-6">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.content}</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如有任何问题或建议，欢迎通过以下表单与我们联系
            </p>

            <form id="contact-form-d9rcg3fnahb7382ovkgg" onSubmit={handleSubmit} className="bg-background-50 rounded-xl p-6 md:p-8" data-readdy-form="true">
              <div className="space-y-5">
                <div>
                  <label htmlFor="about-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    您的称呼 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的称呼"
                  />
                </div>
                <div>
                  <label htmlFor="about-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    主题
                  </label>
                  <input
                    id="about-subject"
                    name="subject"
                    type="text"
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入咨询主题"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <input
                  name="phone_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full bg-primary-500 text-background-50 dark:text-foreground-950 py-3 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '提交中...' : '发送留言'}
                </button>
              </div>

              {formStatus === 'success' && (
                <div className="mt-4 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm text-center">
                  留言发送成功！我们会尽快回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mt-4 p-3 rounded-lg bg-primary-100 text-primary-700 text-sm text-center">
                  {formError || '提交失败，请稍后重试'}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
    </>
  );
}